import * as React from 'react';
import styled, { css } from 'styled-components';
import { InputDecorationType } from '../input-decoration';

interface StyledInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  classNameInput?: string;
  active: boolean;
  withDecoration?: InputDecorationType;
  bigDecoration?: boolean;
  biggerDecoration?: boolean;
  tabIndex?: number;
  maxLength?: number;
  propagateClick?: boolean;
  hasTruncate?: boolean;
  disabled?: boolean;
  noLabel?: boolean;
  alignRight?: boolean;
  small?: boolean;
  isDark?: boolean;
  hasError?: boolean;
  yellowValue?: boolean;
  redValue?: boolean;
  ref?: React.LegacyRef<HTMLInputElement>;
}

const StyledInput = styled((props: StyledInputProps) => (
  <input
    className={props.className + ' ' + props.classNameInput}
    id={props.id}
    type={props.type}
    value={props.value}
    placeholder={props.placeholder}
    autoComplete={props.autoComplete}
    tabIndex={props.tabIndex}
    maxLength={props.maxLength}
    disabled={props.disabled}
    onClick={(ev) => (props.propagateClick ? null : ev.stopPropagation())}
    onChange={(ev) => (props.onChange ? props.onChange(ev) : null)}
    onKeyDown={(ev) => (props.onKeyDown ? props.onKeyDown(ev) : null)}
    onFocus={(ev) => (props.onFocus ? props.onFocus(ev) : null)}
    onBlur={(ev) => (props.onBlur ? props.onBlur(ev) : null)}
    min={props.min}
    max={props.max}
    step={props.step}
    ref={props.ref}
    onAnimationStart={props.onAnimationStart}
  />
))`
  width: 100%;
  position: relative;
  border: none;
  border-radius: 4px;
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h4};
  font-weight: 700;
  line-height: 16px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  cursor: auto;
  padding: ${(props) =>
    props.active || props.value || !props.noLabel
      ? '23px 12px 9px 9px'
      : '16px 12px 16px 9px'};

  @keyframes onAutoFillStart {
  }
  @keyframes onAutoFillCancel {
  }
  &:-webkit-autofill {
    // Expose a hook for JavaScript when auto fill is shown.
    // JavaScript can capture 'animationstart' events
    animation-name: onAutoFillStart;

    // Make the backgound color become yellow _really slowly_
    transition: background-color 50000s ease-in-out 0s;
  }
  &:not(:-webkit-autofill) {
    // Expose a hook for JS onAutoFillCancel
    // JavaScript can capture 'animationstart' events
    animation-name: onAutoFillCancel;
  }

  ${(props) =>
    props.yellowValue
      ? css`
          color: ${({ theme }) => theme.colors.primary};
        `
      : null};

  ${(props) =>
    props.redValue
      ? css`
          color: ${({ theme }) => theme.colors.error};
        `
      : null};

  ${(props) =>
    props.withDecoration === 'right' && (props.active || props.value)
      ? css`
          padding: 23px 34px 9px 9px;
        `
      : null};

  ${(props) =>
    props.withDecoration === 'left'
      ? props.bigDecoration
        ? props.noLabel
          ? 'padding: 16px 9px 16px 48px;'
          : 'padding: 23px 9px 9px 48px;'
        : 'padding: 23px 9px 9px 34px;'
      : null}

  ${(props) =>
    props.withDecoration === 'both'
      ? props.bigDecoration
        ? props.noLabel
          ? 'padding: 16px 34px 16px 48px;'
          : 'padding: 23px 34px 9px 48px;'
        : 'padding: 23px 34px 9px 34px;'
      : null}

  ${(props) =>
    props.withDecoration === 'left'
      ? props.biggerDecoration
        ? 'padding: 23px 34px 9px 48px;'
          ? 'padding: 16px 9px 16px 128px;'
          : 'padding: 23px 9px 9px 128px;'
        : null
      : null}

  ${(props) =>
    props.hasTruncate
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : null}

  ${(props) =>
    props.alignRight
      ? css`
          text-align: right;
        `
      : null}
  ${(props) =>
    props.isDark
      ? css`
          color: ${({ theme }) => theme.colors.white};
        `
      : null}

  ${(props) =>
    props.hasError
      ? css`
          color: ${({ theme }) => theme.colors.error};
        `
      : null}

  @media ${({ theme }) => theme.device.tablet} {
    font-size: ${({ theme }) => theme.typography.h4};
    line-height: 16px;
    padding: ${(props) =>
      props.active || props.value || !props.noLabel
        ? '30px 18px 14px 15px'
        : '22px 18px 22px 15px'};

    ${(props) =>
      props.withDecoration === 'right' && (props.active || props.value)
        ? css`
            padding: 30px 40px 14px 15px;
          `
        : null};

    ${(props) =>
      props.withDecoration === 'left'
        ? props.bigDecoration
          ? props.noLabel
            ? css`
                padding: 22px 40px 22px 64px;
              `
            : css`
                padding: 30px 40px 14px 64px;
              `
          : css`
              padding: 30px 15px 14px 45px;
            `
        : null}

    ${(props) =>
      props.withDecoration === 'both'
        ? props.bigDecoration
          ? props.noLabel
            ? css`
                padding: 22px 40px 14px 64px;
              `
            : css`
                padding: 30px 40px 14px 64px;
              `
          : css`
              padding: 30px 15px 14px 45px;
            `
        : null}

    ${(props) =>
      props.withDecoration === 'left'
        ? props.biggerDecoration
          ? css`
              padding: 30px 40px 14px 144px;
            `
          : null
        : null}

        ${(props) =>
      props.small
        ? props.active
          ? css`
              padding-block-start: 25px;
              padding-block-end: 9px;
            `
          : css`
              padding-block-start: 17px;
              padding-block-end: 17px;
            `
        : ''}
  }
`;

export default StyledInput;
